/**
 * `Link` is a wrapper component around `react-router-dom`'s Link.
 * It supports all the original properties and additionally sends an analytics tracking event when clicked.
 *
 * It uses the `useSearchParamOrDefaultProps` hook to extract analytics-related parameters, either from the search params or from the provided default options.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.to - The destination URL or route.
 * @param {string} [props.target='_top'] - The target frame for the link. Default is '_top'.
 * @param {string|JSX.Element} props.children - The text or elements to be rendered within the link.
 *
 * @returns {JSX.Element} The rendered JSX element.
 */

import { useEffect } from 'react';
import { Link as ReactRouterDomLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import useSearchParamOrDefaultProps from '../hooks/useSearchParamOrDefaultProps.js';
import styles from './Link.module.css';
import $t from '../utils/analyticsEventBus.js';

const Link = ({ to, target, styleAsButton, children }) => {
  const {
    analyticsPageId,
    analyticsOutcomeId,
    analyticsTrackingId,
    analyticsOutcomeType,
    analyticsOutcomeDetail,
    analyticsOutcomeSlug,
    callToActionButtonText,
    finalCallToActionButtonText,
    rssFeedArticleLinkColor: linkColor,
    callToActionButtonTextColor: color,
    callToActionButtonBackgroundColor: backgroundColor,
    callToActionButtonBorderRadius: borderRadius,
    callToActionButtonBorderWidth: borderWidth,
    callToActionButtonBorderStyle: borderStyle,
    callToActionButtonBorderColor: borderColor,
  } = useSearchParamOrDefaultProps();

  const analyticsParamOptions = {
    analyticsPageId,
    analyticsOutcomeId,
    analyticsTrackingId,
    analyticsOutcomeType,
    analyticsOutcomeDetail,
    analyticsOutcomeSlug,
  };

  useEffect(() => {
    $t('register', 'phx_zephr_click', {
      allowDuplicate: true,
      remote: true,
    });
  }, []);

  const onClick = () => {
    $t('track', 'phx_zephr_click', {
      ...analyticsParamOptions,
      action: finalCallToActionButtonText || callToActionButtonText,
    });
  };

  const buttonStyle = {
    color,
    backgroundColor,
    borderRadius,
    borderColor,
    borderStyle,
    borderWidth,
  };

  const linkStyle = {
    color: linkColor,
  };

  return (
    <ReactRouterDomLink
      to={to}
      target={target}
      onClick={onClick}
      className={styles.link}
      style={styleAsButton ? buttonStyle : linkStyle}
    >
      {children}
    </ReactRouterDomLink>
  );
};

Link.displayName = 'Link';

Link.propTypes = {
  to: PropTypes.string,
  target: PropTypes.string,
  styleAsButton: PropTypes.bool,
  children: PropTypes.node,
};

Link.defaultProps = {
  to: '',
  target: '_top',
  styleAsButton: true,
  children: null,
};

export default Link;
